import React, { Component, ReactElement, } from "react";

import { classNames, getHash, } from "components/Utils";
import { Button, ButtonColor, SelectInput, } from "components/FormControls";
import {
    DamageMeter,
    DamageStatus,
    DamageType,
    HelpModal,
    HelpModalType,
    Icons,
    TrackerModal,
    TrackerModalType,
    getFilledArray,
} from ".";

export enum TrackerType {
    Health = "health",
    Willpower = "willpower",
};

type TrackerData = {
    type: TrackerType;
    value: number;
    damage: DamageType[];
};

type Props = {
    type: TrackerType,
};

type State = {
    value: number | undefined;
    damage: DamageType[] | undefined;
    visibleModal: TrackerModalType | HelpModalType | undefined;
};

const MAX_HEALTH_ATTRIBUTE = 10;
const MAX_WILLPOWER_ATTRIBUTE = 10;

const HEALTH_ATTRIBUTE_MODIFIER = 0;
const WILLPOWER_ATTRIBUTE_MODIFIER = 0;

export class Tracker extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        const data = retrieveTrackerData(props.type);
        const value = data?.value;
        const damage = data?.damage || getDefaultDamage(value, props.type);

        this.state = {
            value,
            damage,
            visibleModal: undefined,
        };

        // method binding
        this._onAttributeChange = this._onAttributeChange.bind(this);
        this._toggleModal = this._toggleModal.bind(this);
        this._onDamageChange = this._onDamageChange.bind(this);
    }

    public render() {
        const containerCss = classNames([
            "__tracker__",
            "__healthtracker__",
        ]);

        const columnCss = classNames([
            "col",
            "col-12",
            "mb-3"
        ]);

        const attributeCss = classNames([
            columnCss,
            "col-md-4",
            "col-lg-3",
        ]);

        const damageCss = classNames([
            columnCss,
            "col-md-8",
            "col-lg-9",
        ]);

        return (
            <div className={containerCss}>
                <div className="row mb-3">
                    <div className={attributeCss}>
                        {this._renderAttribute()}
                    </div>

                    <div className={damageCss}>
                        {this._renderDamage()}
                    </div>
                </div>

                <div className="row mb-3">
                    {this._renderButtons()}
                </div>

                {this._renderTrackerModal()}
                {this._renderHelpModal()}
            </div>
        );
    }

    private _onAttributeChange(value: number | undefined): void {
        const { type, } = this.props;

        const damage = this._getDamage(value);

        this.setState({
            value,
            damage,
        });

        if (!value) {
            clearTrackerData(type);
            return;
        }

        const data = {
            type,
            value,
            damage,
        } as TrackerData;
        storeTrackerData(data);
    }

    private _getDamage(size: number | undefined): DamageType[] | undefined {
        const { type, } = this.props;

        if (!size) {
            return undefined;
        }

        const data = retrieveTrackerData(type);
        if (data) {
            // we have data, return it
            return data.damage;
        }

        // otherwise return the default
        return getDefaultDamage(size, type);
    }

    // attribute rendering
    private _renderAttribute(): ReactElement {
        const { type, } = this.props;
        const { value, } = this.state;

        const label = getAttributeLabel(type);

        const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
            const newValue = parseInt(e.currentTarget.value, 10);
            this._onAttributeChange(newValue);
        };

        const handleInputClear = () => {
            this._onAttributeChange(undefined);
        };

        return (
            <div className="__attribute__">
                <label className="form-label">{label}</label>
                <SelectInput
                    value={value}
                    onSelectChange={handleSelectChange}
                    onInputClear={handleInputClear}
                >
                    {this._renderAttributeOptions(type)}
                </SelectInput>
            </div>
        );
    }

    private _renderAttributeOptions(type: TrackerType): ReactElement[] {
        const size = type === TrackerType.Health ? MAX_HEALTH_ATTRIBUTE : MAX_WILLPOWER_ATTRIBUTE;
        const options = [...Array(size)].map((el, index) => {
            const num = index + 1;
            return <option key={index} value={num}>{num}</option>;
        });
        return options;
    }

    // damage meter rendering
    private _renderDamage(): ReactElement {
        const { type, } = this.props;
        const { damage, } = this.state;

        const label = getDamageLabel(type);
        const labelCss = classNames([
            "form-label",
            "__damagelabel__",
        ]);
        let labelElement = <label className={labelCss}>{label}</label>;

        // Willpower adds current score (remaining empty slots) to the status
        if (type === TrackerType.Willpower) {
            const score = damage?.filter(value => value === DamageType.None).length;
            const scoreElement = damage !== undefined
                ? <span className="__score__">({score})</span>
                : undefined;
            labelElement = <label className={labelCss}>{label} {scoreElement}</label>;
        }

        return (
            <div className="__damage__">
                <div className="row">
                    <div className="col col-6">
                        {labelElement}
                    </div>

                    <div className="col col-6 text-end">
                        {this._renderDamageStatus()}
                    </div>
                </div>

                <div className="input-group">
                    <DamageMeter damage={damage} />
                </div>
            </div>
        );
    }

    private _renderDamageStatus(): ReactElement {
        const { damage, } = this.state;

        if (!damage) {
            return <></>;
        }

        const status = getDamageStatus(damage);

        let text = "Unhurt";
        let css = "none";

        switch (status) {
            case DamageStatus.Incapacitated:
                text = "Incapacitated"
                css = "incapacitated";
                break;

            case DamageStatus.Impaired:
                text = "Impaired";
                css = "impaired";
                break;

            case DamageStatus.Wounded:
                text = "Wounded";
                css = "wounded";
                break;

            // no damage
            default:
                break;
        }

        const statusCss = classNames([
            css,
            "__status__",
        ]);
        return <span className={statusCss}>{text}</span>;
    }

    private _onDamageChange(damage: DamageType[] | undefined): void {
        const { type, } = this.props;
        const { value, } = this.state;
        this.setState({
            damage,
        });

        const data = {
            type,
            value,
            damage,
        } as TrackerData;
        storeTrackerData(data);
    }

    // buttons rendering
    private _renderButtons(): ReactElement {
        const { type, } = this.props;
        const { damage, } = this.state;

        const columnCss = classNames([
            "col",
            "col-5",
        ]);
        const buttonCss = classNames([
            "w-100",
        ]);

        const handleHealClick = () => {
            this._toggleModal(TrackerModalType.Heal);
        };
        const isHealDisabled = damage?.filter(el => el === DamageType.None).length === damage?.length;

        const handleDamageClick = () => {
            this._toggleModal(TrackerModalType.Damage);
        };
        const isDamageDisabled = damage?.filter(el => el === DamageType.Aggravated).length === damage?.length;

        const handleHelpClick = () => {
            const helpModal = type === TrackerType.Health ? HelpModalType.Health : HelpModalType.Willpower;
            this._toggleModal(helpModal);
        };

        const helpButtonCss = classNames([
            buttonCss,
            "__helpbutton__",
        ]);

        return (
            <>
                <div className={columnCss}>
                    <Button
                        label="Heal"
                        onClick={handleHealClick}
                        disabled={isHealDisabled}
                        className={buttonCss}
                        color={ButtonColor.Success}
                    />
                </div>

                <div className={columnCss}>
                    <Button
                        label="Damage"
                        onClick={handleDamageClick}
                        disabled={isDamageDisabled}
                        className={buttonCss}
                        color={ButtonColor.Danger}
                    />
                </div>

                <div className="col col-2">
                    <Button
                        label="Help"
                        onClick={handleHelpClick}
                        className={helpButtonCss}
                    >
                        <span className="d-block d-md-none">{Icons.Help}</span>
                        <span className="d-none d-md-block">Help</span>
                    </Button>
                </div>
            </>
        );
    }

    private _toggleModal(type: TrackerModalType | HelpModalType | undefined): void {
        const { visibleModal, } = this.state;

        // hide the modal if we're not specifying one to show
        // or if the one we're specifying is already open
        if (!type || type === visibleModal) {
            this.setState({
                visibleModal: undefined,
            });

            return;
        }

        this.setState({
            visibleModal: type,
        });
    }

    // modal rendering
    // damage/heal modal
    private _renderTrackerModal(): ReactElement {
        const { damage, visibleModal, } = this.state;

        const isModalVisible = visibleModal === TrackerModalType.Damage || visibleModal === TrackerModalType.Heal;

        if (!isModalVisible || !damage) {
            return <></>;
        }

        const handleCancel = () => {
            this._toggleModal(undefined);
        };

        const handleApply = (newDamage: DamageType[]) => {
            this._onDamageChange(newDamage);
            this._toggleModal(undefined);
        };

        return (
            <TrackerModal
                type={visibleModal as TrackerModalType}
                damage={damage}
                onCancel={handleCancel}
                onApply={handleApply}
            />
        );
    }

    private _renderHelpModal(): ReactElement {
        const { visibleModal, } = this.state;

        const isModalVisible = visibleModal === HelpModalType.Health
            || visibleModal === HelpModalType.Willpower;

        if (!isModalVisible) {
            return <></>;
        }

        const handleCancel = () => {
            this._toggleModal(undefined);
        };

        return (
            <HelpModal
                type={visibleModal as HelpModalType}
                onCancel={handleCancel}
            />
        );
    }
}

// assorted utility functions
const getAttributeLabel = (type: TrackerType): string => {
    return "Score";
};

const getDamageLabel = (type: TrackerType): string => {
    return type === TrackerType.Health ? "Health" : "Willpower";
};

const getDefaultDamage = (size: number | undefined, type: TrackerType): DamageType[] | undefined => {
    if (!size) {
        return undefined;
    }

    const mod = type === TrackerType.Health ? HEALTH_ATTRIBUTE_MODIFIER : WILLPOWER_ATTRIBUTE_MODIFIER;
    return getFilledArray(size + mod, DamageType.None);
};

const getDamageStatus = (damage: DamageType[] | undefined): DamageStatus => {
    if (!damage) {
        return DamageStatus.None;
    }

    let none = damage.filter(el => el === DamageType.None).length;
    let superficial = damage.filter(el => el === DamageType.Superficial).length;
    let aggravated = damage.filter(el => el === DamageType.Aggravated).length;

    // unhurt
    if (none === damage.length) {
        return DamageStatus.None;
    }

    // incapacitated
    if (aggravated === damage.length) {
        return DamageStatus.Incapacitated;
    }

    // impaired
    if (superficial + aggravated === damage.length) {
        return DamageStatus.Impaired;
    }

    // we've exhausted all other possibilities
    return DamageStatus.Wounded;
};

// localstorage functions
const getStorageItemId = (name: string): string => {
    const hash = getHash(name);
    return `tracker_${hash}`;
};

const storeTrackerData = (data: TrackerData): void => {
    const name = data.type.toLowerCase();
    const id = getStorageItemId(name);
    const json = JSON.stringify(data);

    // this should never be full but just to play it safe...
    try {
        localStorage.setItem(id, json);
    } catch (error) {
        console.error("Error saving tracker data to local storage", error, json);
    }
};

const retrieveTrackerData = (type: TrackerType): TrackerData | undefined => {
    const name = type.toLowerCase();
    const id = getStorageItemId(name);
    const item = localStorage.getItem(id);

    if (!item) {
        return undefined;
    }

    const data = JSON.parse(item);
    return data;
};

const clearTrackerData = (type: TrackerType): void => {
    const name = type.toLowerCase();
    const id = getStorageItemId(name);
    localStorage.removeItem(id);
};

